// src/pages/Vacancies/[id].tsx
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { DndContext, closestCenter, DragEndEvent, useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useAuthStore } from '@/store'
import { apiClient } from '@/api/client'
import { Button } from '@/components/common/UI/Button'
import { Card } from '@/components/common/UI/Card'
import { Badge } from '@/components/common/UI/Badge'
import { Tabs, Tab } from '@/components/common/UI/Tabs'
import { Modal } from '@/components/common/UI/Modal'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import InterviewSlotPanel from '@/components/interview/InterviewSlotPanel'
import VideoInterviewPanel from '@/components/video/VideoInterviewPanel'
import { FormattedText } from '@/components/common/FormattedText'
import { SeoHead } from '@/components/common/SeoHead'

const StageDropColumn: React.FC<{ stage: string; className: string; children: React.ReactNode }> = ({ stage, className, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: stage })
  return <div ref={setNodeRef} className={`${className} ${isOver ? 'ring-2 ring-primary-400' : ''}`}>{children}</div>
}

// Компоненты для канбан-доски
const KanbanBoard: React.FC<{ applications: any[]; stages: string[] }> = ({
  applications,
  stages
}) => {
  const queryClient = useQueryClient()

  // Мутация для перемещения кандидата
  const moveCandidate = useMutation({
    mutationFn: async ({ applicationId, stage }: { applicationId: number; stage: string }) => {
      await apiClient.post(`/v1/applications/${applicationId}/stage`, { stage })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacancy', 'applications'] })
      toast.success('Кандидат перемещен')
    }
  })

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over) return

    const applicationId = Number(active.id)
    const targetApplication = applications.find(app => String(app.id) === String(over.id))
    const newStage = stages.includes(String(over.id))
      ? String(over.id)
      : targetApplication?.current_stage
    const sourceApplication = applications.find(app => Number(app.id) === applicationId)
    if (newStage && newStage !== sourceApplication?.current_stage) {
      moveCandidate.mutate({ applicationId, stage: newStage })
    }
  }

  // Группируем кандидатов по этапам
  const groupedApplications = stages.reduce((acc, stage) => {
    acc[stage] = applications.filter(app => app.current_stage === stage)
    return acc
  }, {} as Record<string, any[]>)

  // Цвета для этапов
  const stageColors: Record<string, string> = {
    new: 'bg-blue-100 text-blue-800',
    interview: 'bg-yellow-100 text-yellow-800',
    test: 'bg-purple-100 text-purple-800',
    offer: 'bg-green-100 text-green-800',
    hired: 'bg-emerald-100 text-emerald-800',
    rejected: 'bg-red-100 text-red-800'
  }

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-5 gap-4 p-4">
        {stages.map((stage) => (
          <StageDropColumn key={stage} stage={stage} className="bg-secondary-50 rounded-lg p-3 min-h-[400px]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-secondary-700 capitalize">
                {stage === 'new' && '🆕 Новые'}
                {stage === 'interview' && '🗣️ Интервью'}
                {stage === 'test' && '📝 Тестовое'}
                {stage === 'offer' && '🎯 Оффер'}
                {stage === 'hired' && '✅ Приняты'}
                {stage === 'rejected' && '❌ Отказ'}
              </h3>
              <Badge className={stageColors[stage]}>
                {groupedApplications[stage]?.length || 0}
              </Badge>
            </div>

            <SortableContext
              items={groupedApplications[stage]?.map(app => app.id) || []}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {groupedApplications[stage]?.map((app) => (
                  <CandidateCard key={app.id} application={app} />
                ))}
              </div>
            </SortableContext>
          </StageDropColumn>
        ))}
      </div>
    </DndContext>
  )
}

// Карточка кандидата
const CandidateCard: React.FC<{ application: any }> = ({ application }) => {
  const [showDetails, setShowDetails] = useState(false)
  const [requiredCriteria, setRequiredCriteria] = useState('')
  const [preferredCriteria, setPreferredCriteria] = useState('')
  const queryClient = useQueryClient()
  const candidate = application.candidate
  const resume = application.resume
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: application.id })
  const dragStyle = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.55 : 1 }

  // Мутация для добавления комментария
  const addComment = useMutation({
    mutationFn: async (content: string) => {
      await apiClient.post(`/v1/resumes/${resume.id}/comments`, { content })
    },
    onSuccess: () => {
      toast.success('Комментарий добавлен')
      queryClient.invalidateQueries({ queryKey: ['resume', resume.id, 'comments'] })
    }
  })

  const aiScreening = useMutation({
    mutationFn: () => apiClient.post<any>(`/v1/ai-assistant/applications/${application.id}/screen`, {
      required: requiredCriteria.split('\n').map(value => value.trim()).filter(Boolean),
      preferred: preferredCriteria.split('\n').map(value => value.trim()).filter(Boolean),
    }),
  })

  const { data: comments = [] } = useQuery({
    queryKey: ['resume', resume.id, 'comments'],
    queryFn: () => apiClient.get(`/v1/resumes/${resume.id}/comments`),
    enabled: !!resume.id && showDetails,
  })

  // Получение скоринга
  const { data: score } = useQuery({
    queryKey: ['resume', resume.id, 'score'],
    queryFn: async () => {
      const response = await apiClient.get(`/v1/resumes/${resume.id}/score`)
      return response
    },
    enabled: !!resume.id
  })

  return (
    <div ref={setNodeRef} style={dragStyle} {...attributes} {...listeners}>
    <Card
      className={`p-3 hover:shadow-md transition-shadow cursor-pointer ${
        score?.match_percentage > 80 ? 'border-l-4 border-success' : ''
      }`}
      onClick={() => setShowDetails(true)}
    >
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-medium text-secondary-900">
            {candidate.first_name} {candidate.last_name}
          </h4>
          <p className="text-sm text-secondary-600">{resume.position}</p>
        </div>
        {score && (
          <div className={`px-2 py-1 rounded text-sm font-medium ${
            score.match_percentage > 80 ? 'bg-success/20 text-success' :
            score.match_percentage > 60 ? 'bg-warning/20 text-warning' :
            'bg-error/20 text-error'
          }`}>
            {score.match_percentage}%
          </div>
        )}
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        {resume.skills?.slice(0, 3).map((skill: string) => (
          <span key={skill} className="text-xs bg-secondary-100 text-secondary-600 px-2 py-0.5 rounded">
            {skill}
          </span>
        ))}
        {resume.skills?.length > 3 && (
          <span className="text-xs text-secondary-400">+{resume.skills.length - 3}</span>
        )}
      </div>

      {/* Модальное окно с деталями */}
      <Modal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        title="Детали кандидата"
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <img
              src={candidate.avatar_url || '/default-avatar.png'}
              alt={candidate.full_name}
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              <h3 className="text-lg font-semibold">{candidate.full_name}</h3>
              <p className="text-secondary-600">{resume.position}</p>
              <p className="text-sm text-secondary-500">
                {resume.experience_level} • {resume.city}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-sm text-secondary-700">Навыки</h4>
              <div className="flex flex-wrap gap-1 mt-1">
                {resume.skills?.map((skill: string) => (
                  <span key={skill} className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium text-sm text-secondary-700">Оценка</h4>
              {score && (
                <div className="mt-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-secondary-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          score.match_percentage > 80 ? 'bg-success' :
                          score.match_percentage > 60 ? 'bg-warning' : 'bg-error'
                        }`}
                        style={{ width: `${score.match_percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{score.match_percentage}%</span>
                  </div>
                  <p className="text-xs text-secondary-500 mt-1">{score.summary}</p>
                </div>
              )}
            </div>
          </div>

          <section className="rounded-lg border border-violet-200 bg-violet-50/50 p-4" onClick={event => event.stopPropagation()}>
            <h4 className="font-semibold">Разбор отклика локальной AI-моделью</h4>
            <p className="mt-1 text-xs text-secondary-600">Резюме обрабатывается только на сервере проекта. Это рекомендация для рекрутера, не автоматическое решение.</p>
            <label className="mt-3 block text-sm font-medium">Обязательные критерии, по одному на строку</label>
            <textarea value={requiredCriteria} onChange={event => setRequiredCriteria(event.target.value)} maxLength={2000} rows={2} className="mt-1 w-full rounded border px-3 py-2 text-sm" placeholder="Опыт продаж B2B от 2 лет&#10;Навык работы с CRM" />
            <label className="mt-2 block text-sm font-medium">Желательные критерии</label>
            <textarea value={preferredCriteria} onChange={event => setPreferredCriteria(event.target.value)} maxLength={2000} rows={2} className="mt-1 w-full rounded border px-3 py-2 text-sm" placeholder="Опыт в SaaS" />
            <Button className="mt-3" disabled={aiScreening.isPending} onClick={() => aiScreening.mutate()}>{aiScreening.isPending ? 'Разбираем…' : 'Сопоставить с критериями'}</Button>
            {aiScreening.isError && <p className="mt-2 text-sm text-error">AI-разбор недоступен. Проверьте локальную модель и права тарифа.</p>}
            {aiScreening.data && <div className="mt-4 space-y-2 rounded bg-white p-3 text-sm"><div className="font-semibold">{aiScreening.data.category === 'match' ? 'Соответствует' : aiScreening.data.category === 'review' ? 'Нужна проверка' : 'Недостаточно данных'} · {aiScreening.data.score}%</div><p>{aiScreening.data.summary}</p>{aiScreening.data.criteria?.map((criterion:any,index:number)=><p key={index} className="text-xs">{criterion.status === 'met' ? '✓' : criterion.status === 'not_met' ? '×' : '?'} {criterion.criterion}: {criterion.evidence}</p>)}{aiScreening.data.follow_up_questions?.length>0&&<div><b>Что уточнить:</b><ul className="list-disc pl-5">{aiScreening.data.follow_up_questions.map((question:string,index:number)=><li key={index}>{question}</li>)}</ul></div>}</div>}
          </section>

          {/* Опыт работы */}
          <div>
            <h4 className="font-medium text-sm text-secondary-700">Опыт работы</h4>
            <div className="space-y-2 mt-1">
              {resume.experience?.slice(0, 3).map((exp: any, idx: number) => (
                <div key={idx} className="text-sm">
                  <p className="font-medium">{exp.position} — {exp.company}</p>
                  <p className="text-secondary-500 text-xs">
                    {exp.start_date} — {exp.end_date || 'по настоящее время'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Комментарии */}
          <div>
            <h4 className="font-medium text-sm text-secondary-700">Комментарии команды</h4>
            <div className="mt-2 space-y-2">
              {comments.map((comment: any) => (
                <div key={comment.id} className="bg-secondary-50 p-2 rounded">
                  <p className="text-sm">{comment.content}</p>
                  <p className="text-xs text-secondary-400 mt-1">
                    {comment.author.full_name} • {new Date(comment.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-2">
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  const form = e.target as HTMLFormElement
                  const input = form.elements.namedItem('comment') as HTMLInputElement
                  if (input.value) {
                    addComment.mutate(input.value)
                    input.value = ''
                  }
                }}
                className="flex gap-2"
              >
                <input
                  name="comment"
                  placeholder="Добавить комментарий..."
                  className="flex-1 px-3 py-2 border border-secondary-300 rounded-lg text-sm"
                />
                <Button type="submit" size="sm">Добавить</Button>
              </form>
            </div>
          </div>
          <InterviewSlotPanel applicationId={application.id} employer />
          <VideoInterviewPanel applicationId={application.id} employer />
        </div>
      </Modal>
    </Card>
    </div>
  )
}

const VacancyABPanel: React.FC<{ vacancy: any }> = ({ vacancy }) => {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const result = useQuery({
    queryKey: ['vacancy', vacancy.id, 'ab-test'],
    queryFn: () => apiClient.get(`/v1/vacancies/${vacancy.id}/ab-test`),
  })
  const create = useMutation({
    mutationFn: () => apiClient.post(`/v1/vacancies/${vacancy.id}/ab-test`, { challenger_title: title, challenger_description: description }),
    onSuccess: () => {
      setTitle('')
      setDescription('')
      queryClient.invalidateQueries({ queryKey: ['vacancy', vacancy.id, 'ab-test'] })
    },
  })
  const test = result.data?.test
  const stats = result.data?.stats
  const totalImpressions = stats ? stats.control.impressions + stats.challenger.impressions : 0

  return <Card className="mb-6 p-5">
    <h2 className="text-lg font-semibold">A/B-тест вакансии</h2>
    <p className="mt-1 text-sm text-secondary-600">Тест сравнивает исходное описание с вариантом. Посетитель всегда видит один и тот же вариант. После 100 просмотров каждого текста выигрывает вариант с большей конверсией в отклик (при отсутствии откликов сравниваются нажатия).</p>
    {!test && <form className="mt-4 space-y-3" onSubmit={(event) => { event.preventDefault(); create.mutate() }}>
      <input required minLength={3} maxLength={255} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Новый заголовок" className="w-full rounded border border-secondary-300 px-3 py-2" />
      <textarea required minLength={50} maxLength={20000} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Вариант описания вакансии (минимум 50 символов)" className="min-h-32 w-full rounded border border-secondary-300 px-3 py-2" />
      <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Создаём…' : 'Запустить тест'}</Button>
      {create.isError && <p className="text-sm text-error">Не удалось запустить тест.</p>}
    </form>}
    {test && <div className="mt-4 space-y-2 text-sm">
      <p>Статус: {test.is_active ? 'идёт сбор данных' : `завершён; победитель — ${test.winner_variant === 'challenger' ? 'новый вариант' : 'исходный текст'}`}</p>
      <div className="grid gap-3 md:grid-cols-2">
        {(['control', 'challenger'] as const).map((variant) => <div key={variant} className="rounded bg-secondary-50 p-3">
          <strong>{variant === 'control' ? 'Исходный текст' : 'Новый вариант'}</strong>
          <p className="mt-1">Просмотры: {stats?.[variant].impressions || 0}</p>
          <p>Нажатия: {stats?.[variant].clicks || 0}</p>
          <p>Отклики: {stats?.[variant].applications || 0}</p>
        </div>)}
      </div>
      {test.is_active && <p className="text-secondary-500">Собрано {totalImpressions} просмотров.</p>}
    </div>}
  </Card>
}

const QuickApplicationCard: React.FC<{ vacancyId: number }> = ({ vacancyId }) => {
  const config = useQuery({ queryKey: ['mass-hiring', vacancyId], queryFn: () => apiClient.get(`/v1/vacancies/${vacancyId}/mass-hiring`) })
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [answers, setAnswers] = useState<string[]>([])
  const [consent, setConsent] = useState(false)
  const submit = useMutation({
    mutationFn: () => apiClient.post(`/v1/vacancies/${vacancyId}/quick-applications`, { full_name: fullName, phone, answers, consent }),
    onSuccess: () => { toast.success('Анкета отправлена'); setFullName(''); setPhone(''); setAnswers([]); setConsent(false) },
  })
  if (!config.data?.is_enabled) return null
  return <Card className="mb-6 p-5">
    <h2 className="text-lg font-semibold">Быстрая анкета</h2>
    <p className="mt-1 text-sm text-secondary-600">Оставьте номер телефона и ответьте на три коротких вопроса.</p>
    <form className="mt-4 space-y-3" onSubmit={(event) => { event.preventDefault(); submit.mutate() }}>
      <input required minLength={2} maxLength={150} value={fullName} onChange={(event) => setFullName(event.target.value)} placeholder="Имя и фамилия" className="w-full rounded border border-secondary-300 px-3 py-2" />
      <input required type="tel" minLength={7} maxLength={30} value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="Телефон для связи" className="w-full rounded border border-secondary-300 px-3 py-2" />
      {config.data.questions.map((question: string, index: number) => <label key={index} className="block text-sm font-medium">
        {question}
        <textarea required maxLength={2000} value={answers[index] || ''} onChange={(event) => setAnswers((current) => { const next = [...current]; next[index] = event.target.value; return next })} className="mt-1 min-h-20 w-full rounded border border-secondary-300 px-3 py-2" />
      </label>)}
      <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} required /><span>Согласен(на) передать мои контактные данные работодателю для связи по этой вакансии.</span></label>
      <Button type="submit" disabled={submit.isPending || !consent}>{submit.isPending ? 'Отправляем…' : 'Отправить анкету'}</Button>
    </form>
  </Card>
}

const MassHiringPanel: React.FC<{ vacancyId: number }> = ({ vacancyId }) => {
  const queryClient = useQueryClient()
  const [enabled, setEnabled] = useState(false)
  const [questions, setQuestions] = useState(['Есть ли у вас опыт работы на этой позиции?', 'Когда вы готовы приступить к работе?', 'Какой график вам подходит?'])
  const [selected, setSelected] = useState<number[]>([])
  const [message, setMessage] = useState('Здравствуйте! Мы получили вашу анкету и хотели бы обсудить вакансию. Когда вам удобно поговорить?')
  const config = useQuery({
    queryKey: ['mass-hiring', vacancyId],
    queryFn: () => apiClient.get(`/v1/vacancies/${vacancyId}/mass-hiring`),
  })
  useEffect(() => {
    if (config.data) {
      setEnabled(config.data.is_enabled)
      if (config.data.questions?.length === 3) setQuestions(config.data.questions)
    }
  }, [config.data])
  const leads = useQuery({ queryKey: ['mass-hiring', vacancyId, 'leads'], queryFn: () => apiClient.get(`/v1/vacancies/${vacancyId}/mass-hiring/leads`) })
  const save = useMutation({
    mutationFn: () => apiClient.put(`/v1/vacancies/${vacancyId}/mass-hiring`, { is_enabled: enabled, questions }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mass-hiring', vacancyId] }),
  })
  const sendSms = useMutation({
    mutationFn: () => apiClient.post(`/v1/vacancies/${vacancyId}/mass-hiring/sms`, { lead_ids: selected, message }),
    onSuccess: (result: any) => { toast.success(`SMS отправлено: ${result.sent}; ошибки: ${result.failed_lead_ids.length}`); setSelected([]); queryClient.invalidateQueries({ queryKey: ['mass-hiring', vacancyId, 'leads'] }) },
  })
  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => apiClient.patch(`/v1/vacancies/${vacancyId}/mass-hiring/leads/${id}`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mass-hiring', vacancyId, 'leads'] }),
  })

  return <Card className="mb-6 p-5">
    <h2 className="text-lg font-semibold">Массовый найм</h2>
    <p className="mt-1 text-sm text-secondary-600">Настройте анкету из трёх вопросов и обрабатывайте кандидатов списком. Массовая SMS-рассылка доступна после настройки Twilio.</p>
    <div className="mt-3 flex items-center gap-2"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} id="mass-hiring-enabled" /><label htmlFor="mass-hiring-enabled">Принимать быстрые анкеты</label></div>
    {questions.map((question, index) => <input key={index} value={question} onChange={(event) => setQuestions((current) => current.map((value, i) => i === index ? event.target.value : value))} maxLength={250} className="mt-2 w-full rounded border border-secondary-300 px-3 py-2" aria-label={`Вопрос ${index + 1}`} />)}
    <Button className="mt-3" onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Сохраняем…' : 'Сохранить анкету'}</Button>
    <h3 className="mt-6 font-medium">Полученные анкеты ({leads.data?.length || 0})</h3>
    <div className="mt-3 space-y-3">{(leads.data || []).map((lead: any) => <div key={lead.id} className="rounded border border-secondary-200 p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2"><label className="flex items-center gap-2"><input type="checkbox" checked={selected.includes(lead.id)} onChange={(event) => setSelected((current) => event.target.checked ? [...current, lead.id] : current.filter((id) => id !== lead.id))} />{lead.full_name} · <a className="text-primary-700" href={`tel:${lead.phone}`}>{lead.phone}</a></label>
      <select value={lead.status} onChange={(event) => updateStatus.mutate({ id: lead.id, status: event.target.value })} className="rounded border border-secondary-300 px-2 py-1"><option value="new">Новый</option><option value="contacted">Связались</option><option value="rejected">Отказ</option><option value="hired">Нанят</option></select></div>
      <ol className="mt-2 list-decimal pl-5">{lead.answers.map((answer: string, index: number) => <li key={index}>{answer}</li>)}</ol>
    </div>)}</div>
    {(leads.data?.length || 0) > 0 && <div className="mt-4 flex flex-col gap-2 sm:flex-row"><textarea value={message} onChange={(event) => setMessage(event.target.value)} maxLength={1600} className="min-h-16 flex-1 rounded border border-secondary-300 px-3 py-2" aria-label="SMS текст" /><Button disabled={!selected.length || sendSms.isPending} onClick={() => sendSms.mutate()}>{sendSms.isPending ? 'Отправляем…' : `Отправить SMS выбранным (${selected.length})`}</Button></div>}
    {sendSms.isError && <p className="mt-2 text-sm text-error">SMS не отправлены. Проверьте настройки Twilio и номера кандидатов.</p>}
  </Card>
}

type BuilderQuestion = { id: string; prompt: string; kind: 'choice' | 'text'; options: string; correct: number; points: number }

const AssessmentPanel: React.FC<{ vacancyId: number; applications: any[] }> = ({ vacancyId, applications }) => {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('Короткое тестовое задание')
  const [instructions, setInstructions] = useState('Ответьте на вопросы в отведённое время.')
  const [questions, setQuestions] = useState<BuilderQuestion[]>([{ id: 'q1', prompt: '', kind: 'choice', options: '', correct: -1, points: 1 }])
  const [selectedTestId, setSelectedTestId] = useState<number | ''>('')
  const [selectedApplications, setSelectedApplications] = useState<number[]>([])
  const [reviewScores, setReviewScores] = useState<Record<number, string>>({})
  const [reviewComments, setReviewComments] = useState<Record<number, string>>({})
  const tests = useQuery({ queryKey: ['assessments', vacancyId], queryFn: () => apiClient.get(`/v1/assessments/vacancies/${vacancyId}`) })
  const create = useMutation({
    mutationFn: () => apiClient.post(`/v1/assessments/vacancies/${vacancyId}`, {
      title, instructions: instructions || null,
      questions: questions.map((question) => question.kind === 'choice'
        ? ({ id: question.id, prompt: question.prompt, kind: question.kind, options: question.options.split('\n').map((value) => value.trim()).filter(Boolean), correct_option: question.correct, points: question.points })
        : ({ id: question.id, prompt: question.prompt, kind: question.kind, points: question.points })),
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assessments', vacancyId] }),
  })
  const assign = useMutation({
    mutationFn: () => apiClient.post(`/v1/assessments/${selectedTestId}/assign`, { application_ids: selectedApplications }),
    onSuccess: () => { setSelectedApplications([]); queryClient.invalidateQueries({ queryKey: ['assessments', vacancyId] }) },
  })
  const review = useMutation({
    mutationFn: ({ assignmentId, score }: { assignmentId: number; score: number }) => apiClient.patch(`/v1/assessments/assignments/${assignmentId}/review`, { manual_score: score, reviewer_comment: reviewComments[assignmentId]?.trim() || null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assessments', vacancyId] }),
  })
  const validQuestionSet = questions.every((question) => {
    if (question.prompt.trim().length < 5) return false
    if (question.kind === 'text') return true
    const optionCount = question.options.split('\n').map((value) => value.trim()).filter(Boolean).length
    return optionCount >= 2 && question.correct >= 0 && question.correct < optionCount
  })
  const changeQuestion = (id: string, changes: Partial<(typeof questions)[number]>) => setQuestions((items) => items.map((question) => question.id === id ? { ...question, ...changes } : question))

  return <Card className="mb-6 p-5">
    <h2 className="text-lg font-semibold">Тестовые задания</h2>
    <p className="mt-1 text-sm text-secondary-600">Создайте тест с автоматической проверкой вариантов ответа, назначьте его кандидатам из откликов. Текстовые ответы можно оценить вручную в результатах.</p>
    <form className="mt-4 space-y-3" onSubmit={(event) => { event.preventDefault(); create.mutate() }}>
      <input required minLength={3} maxLength={200} value={title} onChange={(event) => setTitle(event.target.value)} className="w-full rounded border border-secondary-300 px-3 py-2" aria-label="Название теста" />
      <textarea value={instructions} onChange={(event) => setInstructions(event.target.value)} maxLength={3000} placeholder="Инструкция" className="min-h-16 w-full rounded border border-secondary-300 px-3 py-2" />
      {questions.map((question, index) => {
        const options = question.options.split('\n').map((value) => value.trim()).filter(Boolean)
        return <div key={question.id} className="rounded border border-secondary-200 p-3">
          <div className="mb-2 flex justify-between"><strong>Вопрос {index + 1}</strong>{questions.length > 1 && <button type="button" className="text-sm text-error" onClick={() => setQuestions((items) => items.filter((item) => item.id !== question.id))}>Удалить</button>}</div>
          <input required minLength={5} maxLength={1000} value={question.prompt} onChange={(event) => changeQuestion(question.id, { prompt: event.target.value })} placeholder="Текст вопроса" className="w-full rounded border border-secondary-300 px-3 py-2" />
          <select value={question.kind} onChange={(event) => changeQuestion(question.id, { kind: event.target.value as 'choice' | 'text' })} className="mt-2 rounded border border-secondary-300 px-3 py-2" aria-label="Тип вопроса"><option value="choice">Выбор варианта (автопроверка)</option><option value="text">Развёрнутый ответ (ручная проверка)</option></select>
          {question.kind === 'choice' ? <>
            <textarea required placeholder="Варианты ответа — по одному на строку" value={question.options} onChange={(event) => changeQuestion(question.id, { options: event.target.value, correct: -1 })} className="mt-2 min-h-20 w-full rounded border border-secondary-300 px-3 py-2" />
            <label className="mt-2 flex items-center gap-2 text-sm">Правильный ответ
              <select value={question.correct} onChange={(event) => changeQuestion(question.id, { correct: Number(event.target.value) })} className="rounded border border-secondary-300 px-2 py-1"><option value={-1}>Выберите вариант</option>{options.map((option, optionIndex) => <option key={optionIndex} value={optionIndex}>{option}</option>)}</select>
            </label>
          </> : <p className="mt-2 text-sm text-secondary-500">Ответ будет проверен и оценён работодателем.</p>}
        </div>
      })}
      {questions.length < 30 && <Button type="button" variant="outline" onClick={() => setQuestions((items) => [...items, { id: crypto.randomUUID(), prompt: '', kind: 'choice', options: '', correct: -1, points: 1 }])}>Добавить вопрос</Button>}
      <Button type="submit" disabled={create.isPending || !validQuestionSet}>{create.isPending ? 'Создаём…' : 'Сохранить тест'}</Button>
      {create.isError && <p className="text-sm text-error">Не удалось создать тест. Для каждого вопроса нужны минимум два варианта и правильный ответ.</p>}
    </form>
    {(tests.data || []).length > 0 && <div className="mt-6 border-t border-secondary-200 pt-4">
      <h3 className="font-medium">Назначить тест кандидатам</h3>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row"><select value={selectedTestId} onChange={(event) => setSelectedTestId(event.target.value ? Number(event.target.value) : '')} className="rounded border border-secondary-300 px-3 py-2"><option value="">Выберите тест</option>{tests.data.filter((test: any) => test.is_active).map((test: any) => <option key={test.id} value={test.id}>{test.title}</option>)}</select>
        <Button disabled={!selectedTestId || !selectedApplications.length || assign.isPending} onClick={() => assign.mutate()}>Назначить выбранным ({selectedApplications.length})</Button></div>
      <div className="mt-3 max-h-44 space-y-1 overflow-auto">{applications.map((application) => <label key={application.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={selectedApplications.includes(application.id)} onChange={(event) => setSelectedApplications((current) => event.target.checked ? [...current, application.id] : current.filter((id) => id !== application.id))} />{application.candidate?.full_name || `Кандидат #${application.id}`} · {application.resume?.position}</label>)}</div>
      {assign.isError && <p className="mt-2 text-sm text-error">Не удалось назначить тест выбранным кандидатам.</p>}
      <div className="mt-5 space-y-4">{tests.data.map((test: any) => <div key={test.id}><h3 className="font-medium">{test.title} · результаты</h3>{test.assignments.map((item: any) => <div key={item.id} className="mt-2 rounded bg-secondary-50 p-3 text-sm">
        <div className="flex flex-wrap justify-between gap-2"><span>{applications.find((app) => app.id === item.application_id)?.candidate?.full_name || `Отклик #${item.application_id}`} · {item.status}</span><span>Автооценка: {item.auto_score == null ? '—' : `${item.auto_score}%`}{item.manual_score != null ? ` · итог: ${item.manual_score}%` : ''}</span></div>
        {item.answers && <div className="mt-2 space-y-1">{test.questions.map((question: any) => <p key={question.id}><strong>{question.prompt}</strong>: {question.kind === 'choice' ? question.options[item.answers[question.id]] : item.answers[question.id]}</p>)}</div>}
        {item.status === 'submitted' && <div className="mt-2 space-y-2"><div className="flex gap-2"><input type="number" min={0} max={100} value={reviewScores[item.id] ?? item.auto_score ?? ''} onChange={(event) => setReviewScores((current) => ({ ...current, [item.id]: event.target.value }))} className="w-28 rounded border border-secondary-300 px-2 py-1" aria-label="Ручная оценка, процентов" /><Button size="sm" disabled={review.isPending} onClick={() => review.mutate({ assignmentId: item.id, score: Number(reviewScores[item.id] ?? item.auto_score ?? 0) })}>{review.isPending ? 'Сохраняем…' : 'Сохранить оценку'}</Button></div><textarea maxLength={5000} value={reviewComments[item.id] ?? ''} onChange={(event) => setReviewComments((current) => ({ ...current, [item.id]: event.target.value }))} placeholder="Комментарий кандидату (необязательно)" className="min-h-20 w-full rounded border border-secondary-300 px-3 py-2" aria-label="Комментарий к оценке" /></div>}
        {item.reviewer_comment && <p className="mt-2">Комментарий: {item.reviewer_comment}</p>}
      </div>)}</div>)}</div>
    </div>}
  </Card>
}

// Главная страница вакансии
const VacancyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { i18n, t } = useTranslation()
  const [translatedVacancy, setTranslatedVacancy] = useState<any>(null)
  const [translationLoading, setTranslationLoading] = useState(false)
  const [analyticsEnabled, setAnalyticsEnabled] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lenjob:cookie-consent:v2') || '{}').analytics === true } catch { return false }
  })
  const [activeTab, setActiveTab] = useState('overview')
  const [applyOpen, setApplyOpen] = useState(false)
  const [resumeId, setResumeId] = useState<number | ''>('')
  const [coverLetter, setCoverLetter] = useState('')
  const [visitorId, setVisitorId] = useState(() => {
    try {
      const consent = JSON.parse(localStorage.getItem('lenjob:cookie-consent:v2') || '{}')
      if (consent.analytics === true) {
        const key = 'lenjob:analytics-visitor'
        const value = localStorage.getItem(key) || crypto.randomUUID()
        localStorage.setItem(key, value)
        return value
      }
    } catch { /* Use an ephemeral identifier until optional analytics is accepted. */ }
    return crypto.randomUUID()
  })

  useEffect(() => {
    const syncConsent = () => {
      try {
        const accepted = JSON.parse(localStorage.getItem('lenjob:cookie-consent:v2') || '{}').analytics === true
        setAnalyticsEnabled(accepted)
        if (accepted) {
          const key = 'lenjob:analytics-visitor'
          const value = localStorage.getItem(key) || crypto.randomUUID()
          localStorage.setItem(key, value)
          setVisitorId(value)
        }
      } catch { setAnalyticsEnabled(false) }
    }
    window.addEventListener('lenjob:cookie-consent', syncConsent)
    return () => window.removeEventListener('lenjob:cookie-consent', syncConsent)
  }, [])

  // Получение вакансии
  const { data: vacancy, isLoading } = useQuery({
    queryKey: ['vacancy', id],
    queryFn: async () => {
      const response = await apiClient.get(`/v1/vacancies/${id}`, { params: analyticsEnabled ? { visitor_id: visitorId } : {} })
      return response
    }
  })

  const translateVacancy = async () => {
    if (!vacancy) return
    setTranslationLoading(true)
    try {
      const translated = await apiClient.post<any>('/v1/ai-assistant/translate-vacancy', {
        target_language: (i18n.resolvedLanguage || i18n.language || 'ru').split('-')[0],
        vacancy: { title: vacancy.title, description: vacancy.description, responsibilities: vacancy.responsibilities || [], requirements: vacancy.requirements || [], conditions: vacancy.conditions || [] },
      })
      setTranslatedVacancy(translated)
    } catch {
      toast.error(t('vacancyTranslation.unavailable'))
    } finally {
      setTranslationLoading(false)
    }
  }

  const { data: funnel } = useQuery({
    queryKey: ['vacancy', id, 'funnel'],
    queryFn: () => apiClient.get(`/v1/analytics/vacancy/${id}/conversion-funnel`, { params: { days: 30 } }),
    enabled: !!id && (user?.role === 'employer' || user?.role === 'admin') && activeTab === 'analytics',
  })

  const { data: abVariant } = useQuery({
    queryKey: ['vacancy', id, 'ab-variant', visitorId],
    queryFn: () => apiClient.get(`/v1/vacancies/${id}/ab-variant`, { params: { visitor_id: visitorId } }),
    enabled: !!id && analyticsEnabled,
  })

  useEffect(() => {
    if (analyticsEnabled && abVariant?.test_id && user?.id !== vacancy?.company?.owner_id) apiClient.post(`/v1/vacancies/${id}/ab-event`, { visitor_id: visitorId, event_type: 'impression' }).catch(() => undefined)
  }, [analyticsEnabled, abVariant?.test_id, id, visitorId, user?.id, vacancy?.company?.owner_id])

  const { data: resumes = [] } = useQuery({
    queryKey: ['resumes', 'my'],
    queryFn: () => apiClient.get('/v1/resumes/my', { params: { size: 100 } }),
    enabled: user?.role === 'candidate' && applyOpen,
  })

  // Получение откликов для ATS (только для работодателя)
  const { data: applications } = useQuery({
    queryKey: ['vacancy', id, 'applications'],
    queryFn: async () => {
      const response = await apiClient.get(`/v1/applications/vacancy/${id}`)
      return response
    },
    enabled: user?.role === 'employer' || user?.role === 'admin'
  })

  // Мутация для отклика (соискатель)
  const applyMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post('/v1/applications', { vacancy_id: Number(id), resume_id: resumeId, cover_letter: coverLetter || undefined })
    },
    onSuccess: () => {
      if (analyticsEnabled && abVariant?.test_id) apiClient.post(`/v1/vacancies/${id}/ab-event`, { visitor_id: visitorId, event_type: 'application' }).catch(() => undefined)
      toast.success('Отклик отправлен!')
      setApplyOpen(false)
      navigate('/dashboard')
    }
  })

  const coverLetterMutation = useMutation({
    mutationFn: async () => apiClient.post(`/v1/resumes/${resumeId}/cover-letter`, null, { params: { vacancy_id: Number(id) } }),
    onSuccess: (result: any) => setCoverLetter(result.cover_letter || ''),
    onError: () => toast.error('Не удалось сгенерировать письмо. Можно написать его вручную.')
  })

  if (isLoading) {
    return <div className="flex items-center justify-center h-64">Загрузка...</div>
  }

  if (!vacancy) {
    return <div className="text-center py-12">Вакансия не найдена</div>
  }

  const stages = ['new', 'review', 'interview', 'test', 'offer', 'hired', 'rejected']
  const applicationItems = applications?.items || []

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <SeoHead title={`${vacancy.title} — работа в ${vacancy.city || 'России'} | LenJob`} description={`Вакансия «${vacancy.title}»${vacancy.company?.name ? ` в компании ${vacancy.company.name}` : ''}${vacancy.city ? `, ${vacancy.city}` : ''}. ${vacancy.salary_display || ''} Откликнитесь на LenJob.`.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').slice(0, 300)} keywords={`${vacancy.title}, работа ${vacancy.city || ''}, вакансия LenJob`} path={`/vacancies/${vacancy.id}`} noIndex={!vacancy.is_active || vacancy.moderation_status !== 'approved'} />
      {/* Заголовок */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">{translatedVacancy?.title || abVariant?.title || vacancy.title}</h1>
            <div className="flex items-center gap-4 mt-2">
              <span className="text-secondary-600">{vacancy.company.name}</span>
              <span className="text-secondary-400">•</span>
              <span className="text-secondary-600">{vacancy.city}</span>
              <span className="text-secondary-400">•</span>
              <span className="text-success font-medium">{vacancy.salary_display}</span>
            </div>
          </div>
          {user?.role === 'candidate' && (
            <Button
              onClick={() => {
                if (analyticsEnabled) apiClient.post(`/v1/analytics/vacancy/${id}/events`, { visitor_id: visitorId, event_type: 'click' }).catch(() => undefined)
                if (analyticsEnabled && abVariant?.test_id) apiClient.post(`/v1/vacancies/${id}/ab-event`, { visitor_id: visitorId, event_type: 'click' }).catch(() => undefined)
                setApplyOpen(true)
              }}
            >
              Откликнуться
            </Button>
          )}
        </div>

        {(i18n.resolvedLanguage || i18n.language) !== 'ru' && <div className="mt-4"><Button variant="outline" size="sm" onClick={translateVacancy} disabled={translationLoading}>{translationLoading ? t('vacancyTranslation.translating') : translatedVacancy ? t('vacancyTranslation.update') : t('vacancyTranslation.translate')}</Button>{translatedVacancy && <span className="ml-2 text-xs text-secondary-500">{t('vacancyTranslation.automatic')}</span>}</div>}

        <div className="flex gap-2 mt-4">
          {vacancy.key_skills?.map((skill: string) => (
            <span key={skill} className="px-2 py-1 bg-primary-50 text-primary-700 rounded text-sm">
              {skill}
            </span>
          ))}
        </div>
      </div>

      {user?.role === 'candidate' && <QuickApplicationCard vacancyId={vacancy.id} />}
      {user?.id === vacancy.company.owner_id && <MassHiringPanel vacancyId={vacancy.id} />}
      {user?.id === vacancy.company.owner_id && <AssessmentPanel vacancyId={vacancy.id} applications={applicationItems} />}

      {/* Вкладки */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tab value="overview">Описание</Tab>
        <Tab value="requirements">Требования</Tab>
        {(user?.role === 'employer' || user?.role === 'admin') && (
          <Tab value="candidates">Кандидаты ({applications?.total || 0})</Tab>
        )}
        <Tab value="analytics">Аналитика</Tab>
      </Tabs>

      <div className="mt-6">
        {user?.id === vacancy.company.owner_id && <VacancyABPanel vacancy={vacancy} />}
        {activeTab === 'overview' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div>
              <h3 className="text-xl font-semibold text-secondary-900">Описание вакансии</h3>
              <FormattedText text={translatedVacancy?.description || abVariant?.description || vacancy.description} className="mt-3 text-secondary-700" />

              {vacancy.responsibilities?.length > 0 && (
                <>
                  <h4 className="mb-3 mt-7 text-lg font-semibold text-secondary-900">Обязанности</h4>
                  <ul className="list-disc space-y-2 pl-6 text-secondary-700 marker:text-primary-600">
                    {(translatedVacancy?.responsibilities || vacancy.responsibilities).map((item: string, idx: number) => (
                      <li key={idx} className="pl-1 leading-7">{item}</li>
                    ))}
                  </ul>
                </>
              )}

              {vacancy.conditions?.length > 0 && (
                <>
                  <h4 className="mb-3 mt-7 text-lg font-semibold text-secondary-900">Условия работы</h4>
                  <ul className="list-disc space-y-2 pl-6 text-secondary-700 marker:text-primary-600">
                    {(translatedVacancy?.conditions || vacancy.conditions).map((item: string, idx: number) => (
                      <li key={idx} className="pl-1 leading-7">{item}</li>
                    ))}
                  </ul>
                </>
              )}

              <div className="grid grid-cols-2 gap-4 mt-4 p-4 bg-secondary-50 rounded-lg">
                <div>
                  <span className="text-sm text-secondary-500">Тип занятости</span>
                  <p className="font-medium">{vacancy.employment_types?.join(', ')}</p>
                </div>
                <div>
                  <span className="text-sm text-secondary-500">График работы</span>
                  <p className="font-medium">{vacancy.work_schedules?.join(', ')}</p>
                </div>
                <div>
                  <span className="text-sm text-secondary-500">Опыт</span>
                  <p className="font-medium">{vacancy.experience_level}</p>
                </div>
                {vacancy.remote_available && (
                  <div>
                    <span className="text-sm text-secondary-500">Удаленная работа</span>
                    <p className="font-medium text-success">Доступна</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'requirements' && (
          <section className="rounded-lg bg-white p-6 shadow-sm" aria-labelledby="vacancy-requirements-title">
            <h3 id="vacancy-requirements-title" className="text-xl font-semibold text-secondary-900">Требования к кандидату</h3>
            {vacancy.requirements?.length ? (
              <ul className="mt-4 list-disc space-y-3 pl-6 text-secondary-700 marker:text-primary-600">
                {(translatedVacancy?.requirements || vacancy.requirements).map((item: string, idx: number) => (
                  <li key={idx} className="pl-1 leading-7">{item}</li>
                ))}
              </ul>
            ) : <p className="mt-3 leading-7 text-secondary-600">Работодатель не указал отдельные требования.</p>}
          </section>
        )}

        {activeTab === 'candidates' && applications && (
          <div className="bg-white rounded-lg shadow-sm">
            <KanbanBoard
              applications={applicationItems}
              stages={stages}
            />
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4">
                <p className="text-sm text-secondary-500">Уникальные просмотры за 30 дней</p>
                <p className="text-2xl font-bold">{funnel?.counts.views ?? vacancy.views_count}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-secondary-500">Отклики за 30 дней</p>
                <p className="text-2xl font-bold">{funnel?.counts.applications ?? vacancy.applications_count}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-secondary-500">Конверсия</p>
                <p className="text-2xl font-bold">
                  {funnel?.counts.views > 0 ? Math.round((funnel.counts.applications / funnel.counts.views) * 100) : 0}%
                </p>
              </Card>
            </div>
            {(user?.role === 'employer' || user?.role === 'admin') && <div className="mt-6 border-t border-secondary-200 pt-5">
              <div className="mb-3 flex items-baseline justify-between gap-3"><h3 className="text-lg font-semibold">Воронка за 30 дней</h3><span className="text-xs text-secondary-500">Уникальные посетители, клики и этапы ATS</span></div>
              {funnel ? <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{funnel.stages.map((stage: string) => <Card key={stage} className="p-3">
                <p className="text-sm text-secondary-500">{funnel.stage_labels[stage]}</p>
                <p className="text-2xl font-bold">{funnel.counts[stage]}</p>
                {stage !== 'views' && <p className="text-xs text-secondary-500">Конверсия: {funnel.conversion_rates[stage]}%</p>}
              </Card>)}</div> : <p className="text-sm text-secondary-500">Загружаем аналитику…</p>}
              {funnel?.improvement_hints?.length > 0 && <div className="mt-4 space-y-2"><h4 className="font-medium">Подсказки по вакансии</h4>{funnel.improvement_hints.map((hint: any) => <p key={hint.code} className="rounded bg-amber-50 p-3 text-sm text-amber-900">{hint.message}</p>)}</div>}
            </div>}
          </div>
        )}
      </div>

      <Modal isOpen={applyOpen} onClose={() => setApplyOpen(false)} title="Отклик на вакансию">
        <form className="space-y-4" onSubmit={(event) => {
          event.preventDefault()
          if (resumeId) applyMutation.mutate()
        }}>
          <p className="text-sm text-secondary-600">Выберите резюме, которое увидит работодатель.</p>
          <select value={resumeId} onChange={(event) => setResumeId(event.target.value ? Number(event.target.value) : '')} className="w-full rounded-lg border border-secondary-300 px-3 py-2" required>
            <option value="">Выберите резюме</option>
            {resumes.map((resume: any) => <option key={resume.id} value={resume.id}>{resume.title || resume.position}</option>)}
          </select>
          {resumeId && <div className="space-y-2">
            <div className="flex items-center justify-between"><label htmlFor="cover-letter" className="text-sm font-medium">Сопроводительное письмо</label><Button type="button" size="sm" variant="outline" isLoading={coverLetterMutation.isPending} onClick={() => coverLetterMutation.mutate()}>Создать с AI</Button></div>
            <textarea id="cover-letter" value={coverLetter} onChange={(event) => setCoverLetter(event.target.value)} maxLength={5000} rows={5} className="w-full rounded-lg border border-secondary-300 px-3 py-2" placeholder="Расскажите, почему вам интересна эта вакансия" />
          </div>}
          {resumes.length === 0 && <p className="text-sm text-amber-700">Сначала создайте резюме в личном кабинете.</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setApplyOpen(false)}>Отмена</Button>
            <Button type="submit" isLoading={applyMutation.isPending} disabled={!resumeId || resumes.length === 0}>Отправить отклик</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

export default VacancyDetail
